import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, Wrench, MessageSquare, CreditCard,
  TrendingUp, Eye, Shield, LogOut, Search, CheckCircle,
  XCircle, Clock, ChevronRight, RefreshCw, Calendar,
  Star, Activity, BarChart2, PieChart, AlertTriangle
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

// ── COULEURS ─────────────────────────────────────────────
const C = { orange: '#FF6B00', blue: '#1A3B5D', green: '#10B981', red: '#EF4444', yellow: '#F59E0B', purple: '#8B5CF6', teal: '#06B6D4' };
const PIE_COLORS = [C.orange, C.blue, C.green, C.yellow, C.purple];

// ── MOT DE PASSE ADMIN ───────────────────────────────────

// ── HELPERS ──────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString('fr');
const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
const fmtTime = (d) => new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const statusColor = { PENDING: C.yellow, CONFIRMED: C.blue, IN_PROGRESS: C.teal, COMPLETED: C.green, CANCELLED: C.red };
const statusLabel = { PENDING: 'En attente', CONFIRMED: 'Confirmé', IN_PROGRESS: 'En cours', COMPLETED: 'Terminé', CANCELLED: 'Annulé' };

// ── CARD STAT ────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, color = C.orange, trend }) => (
  <motion.div whileHover={{ y: -4 }} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ width: 42, height: 42, borderRadius: 10, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</div>
      {trend !== undefined && (
        <span style={{ fontSize: '0.72rem', color: trend >= 0 ? C.green : C.red, fontWeight: 700 }}>{trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%</span>
      )}
    </div>
    <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'white', lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>{label}</div>
    {sub && <div style={{ fontSize: '0.72rem', color }}>{sub}</div>}
  </motion.div>
);

// ── LOGIN ADMIN ──────────────────────────────────────────
const AdminLogin = ({ onLogin }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/verify-pin', { pin });
      onLogin();
    } catch {
      setError('Code incorrect.');
      setPin('');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#080E18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,107,0,0.3)', borderRadius: 20, padding: '3rem', width: 380, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛡️</div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'white', marginBottom: '0.5rem' }}>ESPACE ADMIN</h1>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', marginBottom: '2rem' }}>ELIM DÉPANNAGE — Accès restreint</p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <input
            type="password" value={pin} onChange={e => setPin(e.target.value)}
            placeholder="Code d'accès admin"
            style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${error ? C.red : 'rgba(255,255,255,0.1)'}`, borderRadius: 10, padding: '0.9rem 1rem', color: 'white', fontSize: '1rem', outline: 'none', textAlign: 'center', letterSpacing: '4px' }}
            autoFocus
          />
          {error && <span style={{ color: C.red, fontSize: '0.8rem' }}>{error}</span>}
          <button type="submit" style={{ background: `linear-gradient(135deg, ${C.orange}, #CC4400)`, border: 'none', borderRadius: 10, padding: '0.9rem', color: 'white', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '1rem', letterSpacing: '2px', cursor: 'pointer' }}>
            ACCÉDER AU DASHBOARD
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// ── DASHBOARD PRINCIPAL ──────────────────────────────────
export default function AdminDashboard() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [bookingsChart, setBookingsChart] = useState([]);
  const [revenueChart, setRevenueChart] = useState({ daily: [], byMethod: {} });
  const [prestataires, setPrestataires] = useState({ data: [], total: 0 });
  const [clients, setClients] = useState({ data: [], total: 0 });
  const [messages, setMessages] = useState({ data: [], total: 0 });
  const [transactions, setTransactions] = useState({ data: [], total: 0 });
  const [bookings, setBookings] = useState({ data: [], total: 0 });
  const [activity, setActivity] = useState(null);
  const [topPrestataires, setTopPrestataires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!authed) return;
    // Injecter le token avant chaque appel
    const { accessToken } = useAuthStore.getState();
    if (accessToken) api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setLoading(true);
    try {
      const [s, r, b, rev, p, c, m, t, bk, act, top] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/stats/registrations'),
        api.get('/admin/stats/bookings'),
        api.get('/admin/stats/revenue'),
        api.get('/admin/prestataires?limit=50'),
        api.get('/admin/clients?limit=50'),
        api.get('/admin/messages?limit=30'),
        api.get('/admin/transactions?limit=30'),
        api.get('/admin/bookings?limit=30'),
        api.get('/admin/activity'),
        api.get('/admin/top-prestataires'),
      ]);
      setStats(s.data); setRegistrations(r.data); setBookingsChart(b.data);
      setRevenueChart(rev.data); setPrestataires(p.data); setClients(c.data);
      setMessages(m.data); setTransactions(t.data); setBookings(bk.data);
      setActivity(act.data); setTopPrestataires(top.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [authed]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Auto-refresh toutes les 60s
  useEffect(() => {
    if (!authed) return;
    const interval = setInterval(() => setRefreshKey(k => k + 1), 60000);
    return () => clearInterval(interval);
  }, [authed]);

  const toggleUser = async (id) => {
    await api.patch(`/admin/users/${id}/toggle`);
    setRefreshKey(k => k + 1);
  };

  const verifyPrestataire = async (id) => {
    await api.patch(`/admin/prestataires/${id}/verify`);
    setRefreshKey(k => k + 1);
  };

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  const navItems = [
    { key: 'overview', icon: <LayoutDashboard size={18} />, label: 'Vue générale' },
    { key: 'analytics', icon: <BarChart2 size={18} />, label: 'Analytics' },
    { key: 'prestataires', icon: <Wrench size={18} />, label: 'Prestataires', count: stats?.users?.prestataires },
    { key: 'clients', icon: <Users size={18} />, label: 'Clients', count: stats?.users?.clients },
    { key: 'bookings', icon: <Calendar size={18} />, label: 'Réservations', count: stats?.bookings?.pending },
    { key: 'messages', icon: <MessageSquare size={18} />, label: 'Messages', count: stats?.messages },
    { key: 'transactions', icon: <CreditCard size={18} />, label: 'Transactions' },
    { key: 'activity', icon: <Activity size={18} />, label: 'Activité récente' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#080E18', display: 'flex', fontFamily: 'var(--font-cond)' }}>

      {/* SIDEBAR */}
      <div style={{ width: 240, background: 'rgba(255,255,255,0.03)', borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 100 }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: C.orange }}>ELIM ADMIN</div>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.2rem' }}>Dashboard de contrôle</div>
        </div>
        <nav style={{ flex: 1, padding: '1rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          {navItems.map(item => (
            <button key={item.key} onClick={() => setTab(item.key)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.7rem 1rem', borderRadius: 10, border: 'none', background: tab === item.key ? `${C.orange}20` : 'transparent', color: tab === item.key ? C.orange : 'rgba(255,255,255,0.5)', cursor: 'pointer', textAlign: 'left', fontSize: '0.88rem', fontWeight: 600, transition: 'all 0.2s', position: 'relative' }}>
              {item.icon} {item.label}
              {item.count > 0 && <span style={{ marginLeft: 'auto', background: C.orange, color: 'white', borderRadius: 20, padding: '1px 7px', fontSize: '0.65rem', fontWeight: 700 }}>{item.count}</span>}
            </button>
          ))}
        </nav>
        <div style={{ padding: '1rem', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={() => { setAuthed(false); setTab('overview'); }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'rgba(255,255,255,0.3)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem' }}>
            <LogOut size={15} /> Déconnexion
          </button>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ marginLeft: 240, flex: 1, padding: '2rem', minHeight: '100vh' }}>

        {/* HEADER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'white', margin: 0 }}>
              {navItems.find(n => n.key === tab)?.label}
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', margin: '0.2rem 0 0' }}>
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <button onClick={() => setRefreshKey(k => k + 1)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '0.6rem 1.2rem', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '0.82rem' }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} /> Actualiser
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>

            {/* ── VUE GÉNÉRALE ── */}
            {tab === 'overview' && stats && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {/* Stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <StatCard icon={<Users size={20} />} label="Total utilisateurs" value={fmt(stats.users.total)} sub={`${fmt(stats.users.clients)} clients · ${fmt(stats.users.prestataires)} presta`} color={C.blue} />
                  <StatCard icon={<Wrench size={20} />} label="Prestataires" value={fmt(stats.users.prestataires)} sub={`${fmt(stats.prestataires.verified)} vérifiés`} color={C.orange} />
                  <StatCard icon={<Calendar size={20} />} label="Réservations" value={fmt(stats.bookings.total)} sub={`${fmt(stats.bookings.pending)} en attente`} color={C.yellow} />
                  <StatCard icon={<CreditCard size={20} />} label="Revenus (FCFA)" value={fmt(stats.transactions.revenue)} sub={`${fmt(stats.transactions.success)} paiements réussis`} color={C.green} />
                  <StatCard icon={<MessageSquare size={20} />} label="Messages" value={fmt(stats.messages)} color={C.teal} />
                  <StatCard icon={<Star size={20} />} label="Avis clients" value={fmt(stats.reviews)} color={C.yellow} />
                  <StatCard icon={<CheckCircle size={20} />} label="Missions terminées" value={fmt(stats.bookings.completed)} color={C.green} />
                  <StatCard icon={<XCircle size={20} />} label="Annulations" value={fmt(stats.bookings.cancelled)} color={C.red} />
                </div>

                {/* Graphe inscriptions */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                  <h3 style={{ color: 'white', margin: '0 0 1.5rem', fontSize: '1rem' }}>Inscriptions — 30 derniers jours</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={registrations}>
                      <defs>
                        <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.blue} stopOpacity={0.3} /><stop offset="95%" stopColor={C.blue} stopOpacity={0} /></linearGradient>
                        <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.orange} stopOpacity={0.3} /><stop offset="95%" stopColor={C.orange} stopOpacity={0} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: '#0D1B2A', border: '1px solid rgba(255,107,0,0.3)', borderRadius: 8, color: 'white' }} />
                      <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                      <Area type="monotone" dataKey="clients" stroke={C.blue} fill="url(#gc)" name="Clients" strokeWidth={2} />
                      <Area type="monotone" dataKey="prestataires" stroke={C.orange} fill="url(#gp)" name="Prestataires" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Top prestataires */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                  <h3 style={{ color: 'white', margin: '0 0 1rem', fontSize: '1rem' }}>🏆 Top Prestataires</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {topPrestataires.slice(0, 5).map((p, i) => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.7rem 1rem', background: 'rgba(255,255,255,0.03)', borderRadius: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: i === 0 ? C.yellow : i === 1 ? 'rgba(255,255,255,0.2)' : `${C.orange}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, color: 'white' }}>{i + 1}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'white', fontSize: '0.9rem', fontWeight: 700 }}>{p.firstName} {p.lastName}</div>
                          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem' }}>{p.city} · {p._count.bookings} réservations</div>
                        </div>
                        <div style={{ color: C.yellow, fontSize: '0.85rem', fontWeight: 700 }}>⭐ {p.ratingAvg?.toFixed(1)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── ANALYTICS ── */}
            {tab === 'analytics' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {/* Réservations */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                  <h3 style={{ color: 'white', margin: '0 0 1.5rem', fontSize: '1rem' }}>Réservations — 30 derniers jours</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={bookingsChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: '#0D1B2A', border: '1px solid rgba(255,107,0,0.3)', borderRadius: 8, color: 'white' }} />
                      <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                      <Bar dataKey="total" fill={C.blue} name="Total" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="completed" fill={C.green} name="Terminées" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="cancelled" fill={C.red} name="Annulées" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Revenus */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                    <h3 style={{ color: 'white', margin: '0 0 1.5rem', fontSize: '1rem' }}>Revenus FCFA — 30 derniers jours</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={revenueChart.daily}>
                        <defs>
                          <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={C.green} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={C.green} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                        <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#0D1B2A', border: '1px solid rgba(255,107,0,0.3)', borderRadius: 8, color: 'white' }} formatter={v => `${fmt(v)} FCFA`} />
                        <Area type="monotone" dataKey="amount" stroke={C.green} fill="url(#gr)" name="Revenus" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                    <h3 style={{ color: 'white', margin: '0 0 1.5rem', fontSize: '1rem' }}>Par méthode de paiement</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <RePieChart>
                        <Pie data={Object.entries(revenueChart.byMethod).map(([k, v]) => ({ name: k, value: v }))} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                          {Object.keys(revenueChart.byMethod).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: '#0D1B2A', border: '1px solid rgba(255,107,0,0.3)', borderRadius: 8, color: 'white' }} formatter={v => `${fmt(v)} FCFA`} />
                        <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Visiteurs simulés */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    <h3 style={{ color: 'white', margin: 0, fontSize: '1rem' }}>Visiteurs du site</h3>
                    <span style={{ background: `${C.yellow}20`, color: C.yellow, fontSize: '0.65rem', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>Intégrer Google Analytics pour données réelles</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                    {[
                      { label: 'Pages vues / mois', val: '~12 400', icon: <Eye size={18} /> },
                      { label: 'Visiteurs uniques', val: '~3 200', icon: <Users size={18} /> },
                      { label: 'Taux de rebond', val: '~42%', icon: <TrendingUp size={18} /> },
                      { label: 'Durée moy. session', val: '~3min 20s', icon: <Clock size={18} /> },
                    ].map((s, i) => (
                      <div key={i} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: '1rem', textAlign: 'center' }}>
                        <div style={{ color: C.orange, marginBottom: '0.5rem' }}>{s.icon}</div>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'white' }}>{s.val}</div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.2rem' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.72rem', marginTop: '1rem', textAlign: 'center' }}>
                    💡 Pour des données réelles de visiteurs, intègre <strong style={{ color: C.orange }}>Google Analytics 4</strong> ou <strong style={{ color: C.orange }}>Plausible.io</strong> dans ton index.html
                  </p>
                </div>
              </div>
            )}

            {/* ── PRESTATAIRES ── */}
            {tab === 'prestataires' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }} />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un prestataire..."
                      style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '0.7rem 1rem 0.7rem 2.5rem', color: 'white', outline: 'none', fontSize: '0.88rem', boxSizing: 'border-box' }} />
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>{prestataires.total} total</span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        {['Nom', 'Email', 'Ville', 'Note', 'Réservations', 'Vérifié', 'Actif', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '0.9rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '1px', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {prestataires.data?.filter(p => !search || `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase())).map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '0.8rem 1rem', color: 'white', fontSize: '0.88rem', fontWeight: 600 }}>{p.firstName} {p.lastName}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>{p.user?.email}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>{p.city}</td>
                          <td style={{ padding: '0.8rem 1rem', color: C.yellow, fontSize: '0.85rem' }}>⭐ {p.ratingAvg?.toFixed(1)} ({p.ratingCount})</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{p._count?.bookings}</td>
                          <td style={{ padding: '0.8rem 1rem' }}>
                            {p.isVerified
                              ? <span style={{ color: C.green, fontSize: '0.75rem', fontWeight: 700 }}>✓ Vérifié</span>
                              : <button onClick={() => verifyPrestataire(p.id)} style={{ background: `${C.orange}20`, border: `1px solid ${C.orange}40`, color: C.orange, borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer' }}>Vérifier</button>}
                          </td>
                          <td style={{ padding: '0.8rem 1rem' }}>
                            <span style={{ color: p.user?.isActive ? C.green : C.red, fontSize: '0.75rem', fontWeight: 700 }}>
                              {p.user?.isActive ? '● Actif' : '● Inactif'}
                            </span>
                          </td>
                          <td style={{ padding: '0.8rem 1rem' }}>
                            <button onClick={() => toggleUser(p.user?.id)} style={{ background: p.user?.isActive ? `${C.red}20` : `${C.green}20`, border: `1px solid ${p.user?.isActive ? C.red : C.green}40`, color: p.user?.isActive ? C.red : C.green, borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer' }}>
                              {p.user?.isActive ? 'Désactiver' : 'Activer'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── CLIENTS ── */}
            {tab === 'clients' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }} />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un client..."
                      style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '0.7rem 1rem 0.7rem 2.5rem', color: 'white', outline: 'none', fontSize: '0.88rem', boxSizing: 'border-box' }} />
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>{clients.total} total</span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        {['Nom', 'Email', 'Téléphone', 'Ville', 'Réservations', 'Inscription', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '0.9rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '1px', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {clients.data?.filter(c => !search || `${c.firstName} ${c.lastName}`.toLowerCase().includes(search.toLowerCase())).map(c => (
                        <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '0.8rem 1rem', color: 'white', fontSize: '0.88rem', fontWeight: 600 }}>{c.firstName} {c.lastName}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>{c.user?.email}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>{c.user?.phone}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>{c.city}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{c._count?.bookings}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}>{fmtDate(c.user?.createdAt)}</td>
                          <td style={{ padding: '0.8rem 1rem' }}>
                            <button onClick={() => toggleUser(c.user?.id)} style={{ background: c.user?.isActive ? `${C.red}20` : `${C.green}20`, border: `1px solid ${c.user?.isActive ? C.red : C.green}40`, color: c.user?.isActive ? C.red : C.green, borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer' }}>
                              {c.user?.isActive ? 'Désactiver' : 'Activer'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── RÉSERVATIONS ── */}
            {tab === 'bookings' && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {['Client', 'Prestataire', 'Service', 'Statut', 'Montant', 'Date'].map(h => (
                        <th key={h} style={{ padding: '0.9rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '1px', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.data?.map(b => (
                      <tr key={b.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '0.8rem 1rem', color: 'white', fontSize: '0.85rem' }}>{b.client?.firstName} {b.client?.lastName}</td>
                        <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{b.prestataire?.firstName} {b.prestataire?.lastName}</td>
                        <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>{b.service?.replace(/_/g, ' ')}</td>
                        <td style={{ padding: '0.8rem 1rem' }}>
                          <span style={{ background: `${statusColor[b.status]}20`, color: statusColor[b.status], borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>{statusLabel[b.status]}</span>
                        </td>
                        <td style={{ padding: '0.8rem 1rem', color: C.green, fontSize: '0.85rem' }}>{b.amount ? `${fmt(b.amount)} FCFA` : '—'}</td>
                        <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}>{fmtTime(b.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── MESSAGES ── */}
            {tab === 'messages' && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {['Expéditeur', 'Destinataire', 'Message', 'Lu', 'Date'].map(h => (
                        <th key={h} style={{ padding: '0.9rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '1px', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {messages.data?.map(m => {
                      const senderName = m.sender?.client ? `${m.sender.client.firstName} ${m.sender.client.lastName}` : m.sender?.prestataire ? `${m.sender.prestataire.firstName} ${m.sender.prestataire.lastName}` : '?';
                      const receiverName = m.receiver?.client ? `${m.receiver.client.firstName} ${m.receiver.client.lastName}` : m.receiver?.prestataire ? `${m.receiver.prestataire.firstName} ${m.receiver.prestataire.lastName}` : '?';
                      return (
                        <tr key={m.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '0.8rem 1rem', color: 'white', fontSize: '0.85rem' }}>{senderName} <span style={{ color: C.orange, fontSize: '0.7rem' }}>({m.sender?.role})</span></td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{receiverName}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', maxWidth: 300 }}>{m.content?.slice(0, 80)}{m.content?.length > 80 ? '...' : ''}</td>
                          <td style={{ padding: '0.8rem 1rem' }}><span style={{ color: m.isRead ? C.green : C.yellow, fontSize: '0.75rem' }}>{m.isRead ? '✓ Lu' : '● Non lu'}</span></td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}>{fmtTime(m.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── TRANSACTIONS ── */}
            {tab === 'transactions' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {stats && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    <StatCard icon={<CreditCard size={20} />} label="Total transactions" value={fmt(stats.transactions.total)} color={C.blue} />
                    <StatCard icon={<CheckCircle size={20} />} label="Réussies" value={fmt(stats.transactions.success)} color={C.green} />
                    <StatCard icon={<TrendingUp size={20} />} label="Revenus totaux" value={`${fmt(stats.transactions.revenue)} FCFA`} color={C.orange} />
                  </div>
                )}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        {['Client', 'Prestataire', 'Montant', 'Méthode', 'Statut', 'Date'].map(h => (
                          <th key={h} style={{ padding: '0.9rem 1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '1px', textAlign: 'left', fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.data?.map(t => (
                        <tr key={t.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '0.8rem 1rem', color: 'white', fontSize: '0.85rem' }}>{t.client?.firstName} {t.client?.lastName}</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{t.prestataire?.firstName} {t.prestataire?.lastName}</td>
                          <td style={{ padding: '0.8rem 1rem', color: C.green, fontSize: '0.88rem', fontWeight: 700 }}>{fmt(t.amount)} FCFA</td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>{t.method?.replace(/_/g, ' ')}</td>
                          <td style={{ padding: '0.8rem 1rem' }}>
                            <span style={{ background: t.status === 'SUCCESS' ? `${C.green}20` : t.status === 'FAILED' ? `${C.red}20` : `${C.yellow}20`, color: t.status === 'SUCCESS' ? C.green : t.status === 'FAILED' ? C.red : C.yellow, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>{t.status}</span>
                          </td>
                          <td style={{ padding: '0.8rem 1rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}>{fmtTime(t.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── ACTIVITÉ RÉCENTE ── */}
            {tab === 'activity' && activity && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                  <h3 style={{ color: 'white', margin: '0 0 1rem', fontSize: '1rem' }}>🆕 Dernières inscriptions</h3>
                  {activity.recentUsers?.map(u => {
                    const name = u.client ? `${u.client.firstName} ${u.client.lastName}` : u.prestataire ? `${u.prestataire.firstName} ${u.prestataire.lastName}` : 'Utilisateur';
                    return (
                      <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <div>
                          <div style={{ color: 'white', fontSize: '0.85rem', fontWeight: 600 }}>{name}</div>
                          <div style={{ color: u.role === 'CLIENT' ? C.blue : C.orange, fontSize: '0.7rem', fontWeight: 700 }}>{u.role}</div>
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>{fmtDate(u.createdAt)}</div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                  <h3 style={{ color: 'white', margin: '0 0 1rem', fontSize: '1rem' }}>📋 Dernières réservations</h3>
                  {activity.recentBookings?.map(b => (
                    <div key={b.id} style={{ padding: '0.6rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: 'white', fontSize: '0.82rem', fontWeight: 600 }}>{b.client?.firstName} → {b.prestataire?.firstName}</div>
                        <span style={{ background: `${statusColor[b.status]}20`, color: statusColor[b.status], borderRadius: 20, padding: '1px 7px', fontSize: '0.65rem', fontWeight: 700 }}>{statusLabel[b.status]}</span>
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', marginTop: '0.2rem' }}>{b.service?.replace(/_/g, ' ')} · {fmtDate(b.createdAt)}</div>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.5rem' }}>
                  <h3 style={{ color: 'white', margin: '0 0 1rem', fontSize: '1rem' }}>💬 Derniers messages</h3>
                  {activity.recentMessages?.map(m => {
                    const sender = m.sender?.client?.firstName || m.sender?.prestataire?.firstName || '?';
                    return (
                      <div key={m.id} style={{ padding: '0.6rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <div style={{ color: 'white', fontSize: '0.82rem', fontWeight: 600 }}>{sender} <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>({m.sender?.role})</span></div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', marginTop: '0.2rem' }}>{m.content?.slice(0, 60)}...</div>
                        <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.68rem', marginTop: '0.2rem' }}>{fmtTime(m.createdAt)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
        table { table-layout: auto; }
        tr:hover td { background: rgba(255,255,255,0.02); }
      `}</style>
    </div>
  );
}
